import type { MemoryFrameViewState } from "@/lib/memory-frame-view";

export interface MentionedFrame {
  frame_id: string;
  title: string;
  frame_type: string;
  /** Memory frames only: the live view, attached when the prompt is sent. */
  view_state?: MemoryFrameViewState;
}

export interface FrameMentionOption extends MentionedFrame {
  label: string;
}

export interface FrameMentionSource {
  frame_id: string;
  title?: string | null;
  frame_type: string;
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

export function frameMentionOptionsFromSources(
  sources: FrameMentionSource[],
): FrameMentionOption[] {
  const baseItems = sources
    .map((source) => ({
      frame_id: source.frame_id,
      title: source.title?.trim() || "Untitled",
      baseLabel: source.title?.trim() || "Untitled",
      hasExplicitTitle: Boolean(source.title?.trim()),
      frame_type: source.frame_type,
    }))
    .filter((frame) => frame.baseLabel.length > 0);
  const counts = new Map<string, number>();
  for (const item of baseItems) {
    const key = item.baseLabel.toLowerCase();
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }

  return baseItems.map((item) => {
    const duplicate = (counts.get(item.baseLabel.toLowerCase()) ?? 0) > 1;
    const suffix = item.frame_id.slice(0, 8);
    const label =
      duplicate || !item.hasExplicitTitle
        ? `${item.baseLabel} #${suffix}`
        : item.baseLabel;
    return {
      frame_id: item.frame_id,
      title: item.title,
      label,
      frame_type: item.frame_type,
    };
  });
}

export function filterFrameMentionOptions(
  frames: FrameMentionOption[],
  query: string,
): FrameMentionOption[] {
  const q = query.trim().toLowerCase();
  const filtered = q
    ? frames.filter((frame) => frame.label.toLowerCase().includes(q))
    : frames;
  return filtered.slice(0, 8);
}

export function parseFrameMentions(
  text: string,
  frames: FrameMentionOption[],
): MentionedFrame[] {
  if (!text || frames.length === 0) return [];

  const sorted = [...frames].sort((a, b) => b.label.length - a.label.length);
  const pattern = new RegExp(
    `(?<=\\s|^)@(${sorted.map((f) => escapeRegExp(f.label)).join("|")})(?=\\s|$)`,
    "gi",
  );
  const byLabel = new Map(sorted.map((f) => [f.label.toLowerCase(), f]));
  const seen = new Set<string>();
  const mentions: MentionedFrame[] = [];
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(text)) !== null) {
    const label = match[1]?.toLowerCase();
    const frame = label ? byLabel.get(label) : undefined;
    if (!frame || seen.has(frame.frame_id)) continue;
    seen.add(frame.frame_id);
    mentions.push({
      frame_id: frame.frame_id,
      title: frame.title,
      frame_type: frame.frame_type,
    });
  }

  return mentions;
}
